/* global CUSTOMER_API_URL */

import HTTPError from 'errors/HTTPError';

const fetchCustomerApi = (
  token,
  accountId,
  path,
  method = 'GET',
  { body, download, form } = {}
) =>
  fetch(`${CUSTOMER_API_URL}/v1/accounts/${accountId}/${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      /**
       * NOTE: When form data is used, omit content type to ensure the browser
       *       properly handles adding the header boundary value. Also for
       *       downloads requests, set the content type to zip (which is the
       *       only format we use for downloads at the moment)
       */
      ...(!form && {
        'Content-Type': download ? 'application/zip' : 'application/json',
      }),
    },

    // NOTE: Do not stringify the body if FormData is used
    ...(form && { body: form }),
    ...(body && { body: JSON.stringify(body) }),
  })
    .then(async response => {
      if (!response.ok) {
        const data = await response.json();
        throw new HTTPError({ ...response, data }, data.message);
      }

      // NOTE: When requesting a download, serialize the response to a Blob
      //       rather than the default JSON
      return download ? response.blob() : response.json();
    })
    .then(data => ({ data }));

const getRule = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `rules/${id}`);

const getSegmentMembership = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `segment_memberships/${id}`);

const getAllSegmentMemberships = (token, accountId) =>
  fetchCustomerApi(token, accountId, `segment_memberships`);

const getAllCustomEvents = (token, accountId) =>
  fetchCustomerApi(token, accountId, `custom_events`);

const createChart = (token, accountId, chart) =>
  fetchCustomerApi(token, accountId, `charts`, 'POST', { body: chart });

const getAllCharts = (token, accountId) =>
  fetchCustomerApi(token, accountId, `charts`);

const getChart = (token, accountId, chartId) =>
  fetchCustomerApi(token, accountId, `charts/${chartId}`);

const updateChart = (token, accountId, chartId, delta) =>
  fetchCustomerApi(token, accountId, `charts/${chartId}`, 'PATCH', {
    body: delta,
  });

const deleteChart = (token, accountId, chartId) =>
  fetchCustomerApi(token, accountId, `charts/${chartId}`, 'DELETE');

const getAllSegments = (token, accountId) =>
  fetchCustomerApi(token, accountId, `segments`, 'GET');

const updateSegment = (token, accountId, id, delta) =>
  fetchCustomerApi(token, accountId, `segments/${id}`, 'PATCH', {
    body: delta,
  });

const deleteSegment = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `segments/${id}`, 'DELETE');

const createSegment = (token, accountId, segment) =>
  fetchCustomerApi(token, accountId, `segments`, 'POST', { body: segment });

const getAllGoals = (token, accountId) =>
  fetchCustomerApi(token, accountId, `goals`, 'GET');

const updateGoal = (token, accountId, id, delta) =>
  fetchCustomerApi(token, accountId, `goals/${id}`, 'PATCH', { body: delta });

const deleteGoal = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `goals/${id}`, 'DELETE');

const createGoal = (token, accountId, goal) =>
  fetchCustomerApi(token, accountId, `goals`, 'POST', { body: goal });

const getAllPublishedRules = (token, accountId) =>
  fetchCustomerApi(token, accountId, `rules?sorted=true`, 'GET');

const getAllKeys = (token, accountId) =>
  fetchCustomerApi(token, accountId, `api_keys`, 'GET');

const createKey = (token, accountId, apiKey) =>
  fetchCustomerApi(token, accountId, `api_keys`, 'POST', { body: apiKey });

const getAccountGates = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'gates', 'GET');

const revokeKey = (token, accountId, key) =>
  fetchCustomerApi(token, accountId, `api_keys/${key}/revoke`, 'POST');

const activateKey = (token, accountId, key) =>
  fetchCustomerApi(token, accountId, `api_keys/${key}/activate`, 'POST');

const deleteKey = (token, accountId, key) =>
  fetchCustomerApi(token, accountId, `api_keys/${key}`, 'DELETE');

const getAllProfileAttributeLabels = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'profile_attributes/labels', 'GET');

const updateProfileAttributeLabel = (token, accountId, name, delta) =>
  fetchCustomerApi(
    token,
    accountId,
    `profile_attributes/labels/${name}`,
    'PATCH',
    { body: delta }
  );

const inviteTeammate = (token, accountId, invitation) =>
  fetchCustomerApi(token, accountId, 'invites', 'POST', { body: invitation });

const getAccount = (token, accountId) =>
  fetchCustomerApi(token, accountId, '', 'GET');

const updateAccount = (token, accountId, delta) =>
  fetchCustomerApi(token, accountId, '', 'PATCH', { body: delta });

const getAllExperiences = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'experiences');

/**
 * Flow Builder API Methods
 */
const getAllFlows = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'flows?summary=true');

const getFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}`);

const createFlow = (token, accountId, flow) =>
  fetchCustomerApi(token, accountId, 'flows', 'POST', { body: flow });

const archiveFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/archive`, 'POST');

const unarchiveFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/unarchive`, 'POST');

const deleteFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}`, 'DELETE');

const revertFlow = (token, accountId, flowId, versionId) =>
  fetchCustomerApi(
    token,
    accountId,
    `flows/${flowId}/revert?versionId=${versionId}`,
    'POST'
  );

const updateFlow = (token, accountId, flowId, delta) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}`, 'PATCH', {
    body: delta,
  });

const publishFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/publish`, 'POST');

const unpublishFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/unpublish`, 'POST');

const cloneFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/clone`, 'POST');

const localizeFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/localize`, 'POST');

const createTestFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/test`, 'POST');

const getAllFlowVersions = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/versions`);

const getFlowVersionById = (token, accountId, flowId, versionId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/versions/${versionId}`);

const getFlowContentStatus = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/content-status`);

const createStepGroups = (token, accountId, flowId, stepGroup) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/stepGroups`, 'POST', {
    body: stepGroup,
  });

const createStepChild = (
  token,
  accountId,
  flowId,
  stepGroupType,
  stepGroupId,
  stepChild
) =>
  fetchCustomerApi(
    token,
    accountId,
    `flows/${flowId}/stepGroups/${stepGroupType}/${stepGroupId}/stepChildren`,
    'POST',
    { body: stepChild }
  );

const getAllRules = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'rules');

const updateRule = (token, accountId, ruleId, delta) =>
  fetchCustomerApi(token, accountId, `rules/${ruleId}`, 'PATCH', {
    body: delta,
  });

const getAllNps = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'nps');

const getNps = (token, accountId, npsId) =>
  fetchCustomerApi(token, accountId, `nps/${npsId}`);

const updateNps = (token, accountId, npsId, delta) =>
  fetchCustomerApi(token, accountId, `nps/${npsId}`, 'PATCH', { body: delta });

const createNps = (token, accountId, nps) =>
  fetchCustomerApi(token, accountId, 'nps', 'POST', { body: nps });

const publishNps = (token, accountId, npsId) =>
  fetchCustomerApi(token, accountId, `nps/${npsId}/publish`, 'POST');

const unpublishNps = (token, accountId, npsId) =>
  fetchCustomerApi(token, accountId, `nps/${npsId}/unpublish`, 'POST');

const revertNps = (token, accountId, npsId) =>
  fetchCustomerApi(token, accountId, `nps/${npsId}/revert`, 'POST');

const getTranslationMetaForFlow = (token, accountId, flowId) =>
  fetchCustomerApi(token, accountId, `flows/${flowId}/translations/meta`);

const getAllLocales = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'locales');

const createLocale = (token, accountId, locale) =>
  fetchCustomerApi(token, accountId, 'locales', 'POST', { body: locale });

const deleteLocale = (token, accountId, localeId) =>
  fetchCustomerApi(token, accountId, `locales/${localeId}`, 'DELETE');

const downloadTranslations = (token, accountId, flowId, format) =>
  fetchCustomerApi(
    token,
    accountId,
    `flows/${flowId}/translations/download?format=${format}`,
    'GET',
    { download: true }
  );

const uploadTranslation = (token, accountId, flowId, localeId, translation) => {
  const form = new FormData();
  form.append('locale_id', localeId);
  form.append('file', translation);

  return fetchCustomerApi(
    token,
    accountId,
    `flows/${flowId}/translations`,
    'POST',
    { form }
  );
};

const deleteTranslation = (token, accountId, flowId, translationId) =>
  fetchCustomerApi(
    token,
    accountId,
    `flows/${flowId}/translations/${translationId}`,
    'DELETE'
  );

/**
 * As of 2019-10-23, the API client doesn't deal directly with the auth client.
 * So, to keep the code simple, we're injecting a patched version that handles authentication
 * inside of each call. Any additional arguments well be passed through to the original method.
 *
 * @param auth - an instance of an auth client
 *
 * @return object - contains all methods of the original client, partially applied with the JWT and account id
 *
 *  *
 * Example:
 *   given a method signature of
 *      getFlowVersionById(jwt, accountId, flowId, versionId),
 *
 *   the returned method looks like
 *      getFlowVersionById(flowId, versionId)
 */

export default function createApiClient(auth) {
  const client = {
    getRule,
    getSegmentMembership,
    getAllSegmentMemberships,
    getAllCustomEvents,
    createChart,
    getAllCharts,
    getChart,
    updateChart,
    deleteChart,
    getAllSegments,
    updateSegment,
    deleteSegment,
    createSegment,
    getAllGoals,
    updateGoal,
    deleteGoal,
    createGoal,
    getAllPublishedRules,
    getAllKeys,
    createKey,
    getAccountGates,
    revokeKey,
    activateKey,
    deleteKey,
    getAllProfileAttributeLabels,
    updateProfileAttributeLabel,
    inviteTeammate,
    getAccount,
    updateAccount,
    getAllExperiences,

    // Flow Builder API Methods
    getAllFlows,
    getFlow,
    createFlow,
    archiveFlow,
    unarchiveFlow,
    deleteFlow,
    revertFlow,
    updateFlow,
    publishFlow,
    unpublishFlow,
    cloneFlow,
    localizeFlow,
    createTestFlow,
    getAllFlowVersions,
    getFlowVersionById,
    getFlowContentStatus,
    createStepGroups,
    createStepChild,
    getAllRules,
    updateRule,
    getAllNps,
    getNps,
    updateNps,
    createNps,
    publishNps,
    unpublishNps,
    revertNps,
    getTranslationMetaForFlow,
    getAllLocales,
    createLocale,
    deleteLocale,
    downloadTranslations,
    uploadTranslation,
    deleteTranslation,
  };

  return Object.keys(client).reduce((acc, key) => {
    acc[key] = async (...args) => {
      const token = await auth.getToken();
      const id = Number.parseInt(await auth.getAccountId(), 10);
      return client[key](token, id, ...args);
    };
    return acc;
  }, {});
}
